import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  setDoc,
  limit,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { isCentralDispatchRole, isCentralRole, isOperatorAdminRole, isOperatorRole, PORTAL_ACCESS_ROLES } from './authSlice';
import { normalizeGermanPhoneNumber } from '../../utils/phoneNumber';

// Default-Werte für Treuekarte
const defaultLoyaltyCard = {
  currentRideCount: 0,
  availableDiscounts: 0,
  totalEarnedDiscounts: 0,
  totalRedeemedDiscounts: 0,
  totalSavingsEur: 0,
  lastRideCountedId: null,
  lastUpdated: null
};

// Default-Werte für Treuemeilen-Konto (System 2)
const defaultMilesCard = {
  balance: 0,
  totalEarned: 0,
  totalRedeemedBlocks: 0,
  totalSavingsEur: 0,
  lastRideCountedId: null,
  lastEarnedAt: null,
  lastUpdated: null
};

// Speicher für die letzte Aktualisierung und Daten
const customersCache = {
  lastFetched: null,
  data: [],
  fetchInProgress: false,
  // Cache-Zeit in Millisekunden (5 Minuten)
  cacheDuration: 5 * 60 * 1000
};

const DEFAULT_ORGANIZATION_ID = process.env.REACT_APP_DEFAULT_ORGANIZATION_ID || 'default_organization';

const getCurrentUser = (state) => state.auth?.user || null;

const getCustomerManagingOperatorId = (userData) => (
  userData.managedByOperatorId || userData.createdByOperatorId || userData.operatorId || null
);

const toTimestampOrNull = (value) => {
  if (!value) return null;
  if (value?.toDate || value?.seconds) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(12, 0, 0, 0);
  return Timestamp.fromDate(date);
};

const normalizeMedicalTransportEligibility = (value = {}) => ({
  mobilityMarks: Array.isArray(value.mobilityMarks)
    ? value.mobilityMarks.filter(Boolean)
    : [],
  careLevel: value.careLevel ? String(value.careLevel) : '',
  permanentMobilityImpairment: value.permanentMobilityImpairment === true,
  notes: (value.notes || '').trim()
});

// Stammlokal / Fast-Track (KI-STAMMKUNDEN_FASTTRACK_KONZEPT.md §3, §5): Lena bucht für diese
// Kunden ohne Adress-Rückfragen mit der hinterlegten Abholadresse. Das Backend liest nur
// enabled, pickupAddress, pickupLat/pickupLng, pickupNote und requireLandline.
export const readFastTrackProfile = (userData = {}) => {
  const fastTrack = userData.fastTrack && typeof userData.fastTrack === 'object' ? userData.fastTrack : null;
  const lat = Number(fastTrack?.pickupLat);
  const lng = Number(fastTrack?.pickupLng);
  return {
    isInstitution: userData.customerCategory === 'institution',
    institutionName: userData.institutionName || '',
    fastTrackEnabled: fastTrack?.enabled === true,
    pickupAddress: fastTrack?.pickupAddress || '',
    pickupLat: fastTrack && Number.isFinite(lat) ? lat : null,
    pickupLng: fastTrack && Number.isFinite(lng) ? lng : null,
    pickupNote: fastTrack?.pickupNote || '',
    requireLandline: fastTrack ? fastTrack.requireLandline !== false : true,
    registeredByAi: fastTrack?.registeredBy === 'ai_telephonist'
  };
};

// Schreibfelder fürs users-Doc. Ohne Abholadresse mit Koordinaten wird kein Fast-Track
// gespeichert (null) — ein abgeschalteter bleibt dagegen mit enabled:false stehen, damit
// ihn Lena beim nächsten Anruf nicht neu anlegt.
const buildFastTrackFields = (profile, currentUser, existingFastTrack) => {
  const address = (profile.pickupAddress || '').trim();
  const hasPickup = address && typeof profile.pickupLat === 'number' && typeof profile.pickupLng === 'number';
  return {
    customerCategory: profile.isInstitution ? 'institution' : 'standard',
    institutionName: profile.isInstitution ? (profile.institutionName || '').trim().slice(0, 120) : null,
    fastTrack: hasPickup
      ? {
          enabled: profile.fastTrackEnabled === true,
          pickupAddress: address,
          pickupLat: profile.pickupLat,
          pickupLng: profile.pickupLng,
          pickupNote: (profile.pickupNote || '').trim().slice(0, 200),
          requireLandline: profile.requireLandline !== false,
          ...(existingFastTrack?.registeredBy ? { registeredBy: existingFastTrack.registeredBy } : {}),
          updatedAt: Timestamp.now(),
          updatedBy: currentUser?.uid || null
        }
      : null
  };
};

const isEmptyFastTrackProfile = (profile) =>
  !profile || (!profile.isInstitution && !(profile.pickupAddress || '').trim());

const canCreatePhoneCustomer = (currentUser, isPhoneCustomer) => (
  isCentralRole(currentUser) ||
  ((isCentralDispatchRole(currentUser) || isOperatorRole(currentUser)) && isPhoneCustomer === true)
);

const assertCanManageCustomer = (currentUser, userData, actionName = 'Kunde verwalten') => {
  if (isCentralRole(currentUser)) return;

  const managingOperatorId = getCustomerManagingOperatorId(userData);
  if (
    isOperatorAdminRole(currentUser) &&
    userData.isPhoneCustomer === true &&
    managingOperatorId === currentUser.operatorId
  ) {
    return;
  }

  throw new Error(`${actionName}: Keine Berechtigung fuer diesen Kunden`);
};

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (options = {}, { getState, rejectWithValue }) => {
    try {
      const { forceRefresh = false, simplified = false } = options;
      const currentPath = window.location.pathname;
      const isCustomersPage = currentPath.startsWith('/kunden');
      
      // Verhindert parallele Anfragen
      if (customersCache.fetchInProgress) {
        console.log('Bereits eine Anfrage in Bearbeitung, warte auf deren Abschluss...');
        return getState().customers.customers;
      }
      
      // Prüfe, ob die Kunden bereits geladen wurden und der Cache gültig ist
      const currentState = getState();
      const cacheValid = customersCache.lastFetched && 
                       (Date.now() - customersCache.lastFetched < customersCache.cacheDuration);
      
      if (!forceRefresh && 
          cacheValid && 
          currentState.customers.customers && 
          currentState.customers.customers.length > 0) {
        console.log('Verwende gecachte Kundendaten (letzte Aktualisierung:', 
                 new Date(customersCache.lastFetched).toLocaleTimeString(), ')');
        return currentState.customers.customers;
      }
      
      console.log('Starte fetchCustomers...', { 
        forceRefresh, 
        simplified, 
        currentPath,
        isCustomersPage
      });
      
      // Flag setzen, dass Anfrage läuft
      customersCache.fetchInProgress = true;
      
      // Kunden sind Benutzer mit isDriver=false
      const customersQuery = query(
        collection(db, 'users'),
        where('isDriver', '==', false),
        orderBy('name', 'asc')
      );
      
      console.log('Führe Customers-Query aus...');
      const querySnapshot = await getDocs(customersQuery);
      console.log('Query-Ergebnis:', querySnapshot.size, 'Kunden gefunden');
      const customers = [];

      // Portal-Konten (Admin/Dispatcher/Unternehmer-Sub-Konten) haben ebenfalls
      // isDriver=false und müssen daher hier explizit ausgeschlossen werden.
      const customerDocs = querySnapshot.docs.filter(
        userDoc => !PORTAL_ACCESS_ROLES.includes(userDoc.data().role)
      );

      // Parallele Verarbeitung für bessere Performance
      const customerPromises = customerDocs.map(async (userDoc) => {
        const userData = userDoc.data();
        const userId = userDoc.id;
        
        // Für Debug-Zwecke die ersten beiden Kunden detailliert loggen
        const detailLogging = customers.length < 2;
        if (detailLogging) {
          console.log('Verarbeite Kunden:', userId);
          console.log('Kundendaten:', userData);
        }
        
        // Grundlegende Daten, die wir immer brauchen
        const customerBase = {
          id: userId,
          isOnline: userData.isOnline || false,
          // Sperrstatus aus Firestore lesen. Fehlte hier: Der Wert wurde nur
          // optimistisch durch toggleCustomerBlocked in den Store geschrieben
          // und war nach jedem Neuladen wieder weg — Chip "Gesperrt" und der
          // Filter "Nur gesperrte" zeigten dann nichts an.
          isBlocked: userData.isBlocked === true,
          // Tester-Freigabe des Testmodus (Chip + Filter in der Kundenliste)
          isTestUser: userData.isTestUser === true,
          total_rides: 0, // Wird später berechnet
          lastLogin: userData.lastLogin ? userData.lastLogin.toDate() : null,
          loyaltyCard: userData.loyaltyCard
            ? {
                ...defaultLoyaltyCard,
                ...userData.loyaltyCard,
                lastUpdated: userData.loyaltyCard.lastUpdated
                  ? userData.loyaltyCard.lastUpdated.toDate()
                  : null
              }
            : defaultLoyaltyCard,
          user: {
            id: userId,
            name: userData.name,
            email: userData.email,
            phoneNumber: userData.phoneNumber || '',
            birthDate: userData.birthDate || null,
            address: userData.address || '',
            profile_picture: userData.profilePic || '',
            isPhoneCustomer: userData.isPhoneCustomer || false,
            medicalTransportEligibility: normalizeMedicalTransportEligibility(userData.medicalTransportEligibility),
            operatorId: userData.operatorId || null,
            managedByOperatorId: userData.managedByOperatorId || null,
            createdByOperatorId: userData.createdByOperatorId || null,
            fastTrackProfile: readFastTrackProfile(userData),
            // Firmen-/Rechnungskunden-Anbindung (Chip in der Liste)
            invoiceCustomerId: userData.invoiceCustomerId || null,
            invoiceCustomerName: userData.invoiceCustomerName || null
          }
        };
        
        // Bei vereinfachter Ansicht geben wir die Basisdaten zurück
        if (simplified && !isCustomersPage) {
          return customerBase;
        }
        
        // Array für parallele Datenabfragen
        const dataFetchPromises = [];
        
        // Fahrtenzählung für Kundendetailseite
        if (isCustomersPage) {
          const ridesPromise = getDocs(query(
            collection(db, 'rides'),
            where('customerId', '==', userId),
            limit(100) // Limit für bessere Performance
          ))
          .then(ridesSnapshot => {
            return ridesSnapshot.docs.length;
          })
          .catch(error => {
            console.warn(`Fehler beim Zählen der Fahrten:`, error);
            return 0;
          });
          
          dataFetchPromises.push(ridesPromise.then(count => {
            customerBase.total_rides = count;
          }));
        }
        
        // Alle Daten parallel laden und dann den Kunden zurückgeben
        if (dataFetchPromises.length > 0) {
          await Promise.all(dataFetchPromises);
        }
        
        return customerBase;
      });
      
      // Alle Kunden-Promises parallel ausführen
      const processedCustomers = await Promise.all(customerPromises);
      customers.push(...processedCustomers);
      
      console.log('Kundenverarbeitung abgeschlossen, gefundene Kunden:', customers.length);
      
      // Cache aktualisieren
      customersCache.data = customers;
      customersCache.lastFetched = Date.now();
      customersCache.fetchInProgress = false;
      
      return customers;
    } catch (error) {
      // Fehler-Flag zurücksetzen
      customersCache.fetchInProgress = false;
      
      console.error('Error fetching customers:', error);
      // Vollständigen Fehler zurückgeben für bessere Diagnose
      return rejectWithValue({
        message: error.message,
        code: error.code,
        stack: error.stack,
        details: 'Fehler beim Abrufen der Kunden. Bitte prüfen Sie die Konsole für Details.'
      });
    }
  }
);

export const fetchCustomerById = createAsyncThunk(
  'customers/fetchCustomerById',
  async (customerId, { rejectWithValue }) => {
    try {
      // Kunden werden direkt aus users Sammlung abgefragt
      const userDoc = await getDoc(doc(db, 'users', customerId));

      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }

      const userData = userDoc.data();

      // Prüfen, ob es sich wirklich um einen Kunden handelt (kein Fahrer, kein Portal-Konto)
      if (userData.isDriver || PORTAL_ACCESS_ROLES.includes(userData.role)) {
        throw new Error('Benutzer ist kein Kunde');
      }

      // Fahrten zählen
      const ridesQuery = query(
        collection(db, 'rides'),
        where('customerId', '==', customerId)
      );
      const ridesSnapshot = await getDocs(ridesQuery);
      const totalRides = ridesSnapshot.docs.length;

      // Abgeschlossene Fahrten zählen
      const completedRidesQuery = query(
        collection(db, 'rides'),
        where('customerId', '==', customerId),
        where('status', '==', 'completed')
      );
      const completedRidesSnapshot = await getDocs(completedRidesQuery);
      const completedRides = completedRidesSnapshot.docs.length;

      // Treuekarten-Daten laden oder Defaults verwenden
      const loyaltyCard = userData.loyaltyCard
        ? {
            ...defaultLoyaltyCard,
            ...userData.loyaltyCard,
            lastUpdated: userData.loyaltyCard.lastUpdated
              ? userData.loyaltyCard.lastUpdated.toDate()
              : null
          }
        : defaultLoyaltyCard;

      // Treuemeilen-Konto laden oder Defaults verwenden
      const milesCard = userData.milesCard
        ? {
            ...defaultMilesCard,
            ...userData.milesCard,
            lastUpdated: userData.milesCard.lastUpdated
              ? userData.milesCard.lastUpdated.toDate()
              : null,
            lastEarnedAt: userData.milesCard.lastEarnedAt
              ? userData.milesCard.lastEarnedAt.toDate()
              : null
          }
        : defaultMilesCard;

      return {
        id: customerId,
        isOnline: userData.isOnline || false,
        isBlocked: userData.isBlocked === true,
        // Tester-Freigabe des Testmodus (Schalter in den Kundendetails)
        isTestUser: userData.isTestUser === true,
        lastLogin: userData.lastLogin ? userData.lastLogin.toDate() : null,
        total_rides: totalRides,
        completed_rides: completedRides,
        loyaltyCard,
        milesCard,
        user: {
          id: customerId,
          name: userData.name,
          email: userData.email,
          phoneNumber: userData.phoneNumber || '',
          birthDate: userData.birthDate || null,
          address: userData.address || '',
          profile_picture: userData.profilePic || '',
          isPhoneCustomer: userData.isPhoneCustomer || false,
          medicalTransportEligibility: normalizeMedicalTransportEligibility(userData.medicalTransportEligibility),
          operatorId: userData.operatorId || null,
          managedByOperatorId: userData.managedByOperatorId || null,
          createdByOperatorId: userData.createdByOperatorId || null,
          fastTrackProfile: readFastTrackProfile(userData)
        }
      };
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData, { getState, rejectWithValue, dispatch }) => {
    try {
      const currentUser = getCurrentUser(getState());
      const {
        name,
        phoneNumber,
        birthDate,
        address,
        isPhoneCustomer,
        receiptEmail,
        medicalTransportEligibility,
        fastTrackProfile
      } = customerData;

      if (!canCreatePhoneCustomer(currentUser, isPhoneCustomer)) {
        throw new Error('Keine Berechtigung zum Anlegen dieses Kunden');
      }
      
      let uid, email;
      
      if (isPhoneCustomer) {
        // Für Telefonkunden einen speziellen Präfix verwenden
        uid = 'phone_' + Date.now().toString();
        // Dummy E-Mail für Telefonkunden. Ohne Rufnummer wird sie aus der uid
        // abgeleitet: Patienten aus einer Verordnung haben oft keine hinterlegte
        // Nummer, und `phone_@telephone.local` wäre sonst für alle dieselbe.
        const phoneDigits = String(phoneNumber || '').replace(/\D/g, '');
        email = `${phoneDigits ? `phone_${phoneDigits}` : uid}@telephone.local`;
      } else {
        // Für reguläre Kunden
        uid = customerData.uid || ('customer_' + Date.now().toString());
        email = customerData.email || (name.toLowerCase().replace(/\s+/g, '.') + '@example.com');
      }
      
      // Neuen Benutzer in users-Sammlung erstellen
      // E.164-Form für den schnellen KI-Telefonisten-Match mitschreiben (siehe utils/phoneNumber.js).
      const phoneNumberE164 = normalizeGermanPhoneNumber(phoneNumber);

      const userData = {
        name,
        email,
        // Firestore lehnt `undefined` ab — ohne Rufnummer muss hier ein
        // leerer String stehen.
        phoneNumber: phoneNumber || '',
        ...(phoneNumberE164 ? { phoneNumberE164 } : {}),
        birthDate: toTimestampOrNull(birthDate),
        address: address || '',
        isDriver: false,
        isOnline: false,
        isPhoneCustomer: isPhoneCustomer || false, // Flag für Telefonkunden
        // Quittungs-E-Mail (Quittungsversand 4.5) — nur für Telefonkunden relevant
        ...(isPhoneCustomer && receiptEmail ? { receiptEmail } : {}),
        medicalTransportEligibility: normalizeMedicalTransportEligibility(medicalTransportEligibility),
        ...(!isEmptyFastTrackProfile(fastTrackProfile) ? buildFastTrackFields(fastTrackProfile, currentUser, null) : {}),
        organizationId: currentUser?.organizationId || DEFAULT_ORGANIZATION_ID,
        createdByRole: currentUser?.role || 'system',
        createdBy: currentUser?.uid || null,
        ...(isOperatorRole(currentUser) ? {
          operatorId: currentUser.operatorId,
          createdByOperatorId: currentUser.operatorId,
          managedByOperatorId: currentUser.operatorId
        } : {}),
        // Metadaten
        createdAt: Timestamp.now(),
        lastLogin: Timestamp.now(),
        platform: 'web',
        profilePic: ''
      };
      
      // Benutzer in Firestore speichern
      await setDoc(doc(db, 'users', uid), userData);
      
      // Aktualisierte Kundendaten abrufen
      const customer = {
        id: uid,
        isOnline: false,
        lastLogin: new Date(),
        total_rides: 0,
        isPhoneCustomer: isPhoneCustomer || false,
        user: {
          id: uid,
          name: name,
          email: email,
          phoneNumber: phoneNumber || '',
          birthDate: toTimestampOrNull(birthDate),
          address: address || '',
          profile_picture: '',
          isPhoneCustomer: isPhoneCustomer || false,
          medicalTransportEligibility: normalizeMedicalTransportEligibility(medicalTransportEligibility),
          operatorId: userData.operatorId || null,
          managedByOperatorId: userData.managedByOperatorId || null,
          createdByOperatorId: userData.createdByOperatorId || null
        }
      };
      
      return customer;
    } catch (error) {
      console.error('Error creating customer:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, ...customerData }, { getState, rejectWithValue, dispatch }) => {
    try {
      // Direkte Aktualisierung des Benutzerdokuments
      const userRef = doc(db, 'users', id);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }
      
      const userData = userDoc.data();

      // Prüfen, ob es sich wirklich um einen Kunden handelt (kein Fahrer, kein Portal-Konto)
      if (userData.isDriver || PORTAL_ACCESS_ROLES.includes(userData.role)) {
        throw new Error('Benutzer ist kein Kunde');
      }

      assertCanManageCustomer(getCurrentUser(getState()), userData, 'Kunde bearbeiten');
      
      // Daten für das Benutzerdokument vorbereiten
      const updateData = {};
      
      if (customerData.user) {
        if (customerData.user.name) updateData.name = customerData.user.name;
        if (customerData.user.phoneNumber) {
          updateData.phoneNumber = customerData.user.phoneNumber;
          // E.164 für den KI-Match synchron halten; bei ungültiger Nummer auf null,
          // damit kein veralteter Wert auf einen fremden Anrufer matched.
          updateData.phoneNumberE164 = normalizeGermanPhoneNumber(customerData.user.phoneNumber);
        }
        if (customerData.user.birthDate !== undefined) updateData.birthDate = toTimestampOrNull(customerData.user.birthDate);
        if (customerData.user.address !== undefined) updateData.address = customerData.user.address;
        if (customerData.user.profile_picture) updateData.profilePic = customerData.user.profile_picture;
        // Quittungs-E-Mail (Quittungsversand 4.5): Leeren → null, stoppt den
        // Auto-Versand; der Fahrer-Dialog fragt beim nächsten Abschluss erneut.
        if (customerData.user.receiptEmail !== undefined) {
          updateData.receiptEmail = customerData.user.receiptEmail || null;
        }
        if (customerData.user.medicalTransportEligibility !== undefined) {
          updateData.medicalTransportEligibility = normalizeMedicalTransportEligibility(customerData.user.medicalTransportEligibility);
        }
        // Leeres Formular bei einem Kunden ohne Stammlokal-Daten: nichts schreiben.
        const fastTrackProfile = customerData.user.fastTrackProfile;
        const hadFastTrackData = !!userData.customerCategory || !!userData.fastTrack;
        if (fastTrackProfile !== undefined && (hadFastTrackData || !isEmptyFastTrackProfile(fastTrackProfile))) {
          Object.assign(updateData, buildFastTrackFields(
            fastTrackProfile, getCurrentUser(getState()), userData.fastTrack
          ));
        }
      }
      
      // User-Dokument aktualisieren
      await updateDoc(userRef, {
        ...updateData,
        updated_at: Timestamp.now()
      });
      
      // Aktualisierte Daten abrufen
      return await fetchCustomerById(id)(dispatch);
    } catch (error) {
      console.error('Error updating customer:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id, { getState, rejectWithValue }) => {
    try {
      // Wir markieren den Kunden als deaktiviert, anstatt ihn zu löschen
      // Vollständiges Löschen von Benutzern sollte über Auth-System erfolgen
      const userRef = doc(db, 'users', id);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }

      assertCanManageCustomer(getCurrentUser(getState()), userDoc.data(), 'Kunde deaktivieren');
      
      await updateDoc(userRef, {
        deactivated: true,
        deactivated_at: Timestamp.now()
      });
      
      return id;
    } catch (error) {
      console.error('Error deleting customer:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const toggleCustomerBlocked = createAsyncThunk(
  'customers/toggleCustomerBlocked',
  async ({ id, isBlocked }, { getState, rejectWithValue }) => {
    try {
      const userRef = doc(db, 'users', id);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }

      assertCanManageCustomer(getCurrentUser(getState()), userDoc.data(), 'Kunde sperren');
      await updateDoc(userRef, { isBlocked, updated_at: Timestamp.now() });
      return { id, isBlocked };
    } catch (error) {
      console.error('Fehler beim Ändern des Sperrstatus:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Tester-Freigabe fuer den Testmodus der Kunden-App setzen/entziehen.
 *
 * Das Flag hebt eine globale Sperre auf und bleibt deshalb der Zentrale
 * vorbehalten: `assertCanManageCustomer` laesst Unternehmer-Konten nur an ihre
 * eigenen Telefonkunden, und die Firestore-Rules verweigern das Feld sowohl dem
 * Kunden selbst als auch den Unternehmer-Rollen.
 *
 * Die Freigabe bleibt bestehen, wenn der Testmodus endet — sie kostet dann
 * nichts und greift beim naechsten Mal sofort wieder.
 *
 * nextcab_mobile/docs/general/testmodus_kunden_app_konzept.md
 */
export const toggleCustomerTestUser = createAsyncThunk(
  'customers/toggleCustomerTestUser',
  async ({ id, isTestUser }, { getState, rejectWithValue }) => {
    try {
      const userRef = doc(db, 'users', id);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }

      assertCanManageCustomer(getCurrentUser(getState()), userDoc.data(), 'Tester freischalten');
      await updateDoc(userRef, {
        isTestUser,
        testUserSince: isTestUser ? Timestamp.now() : null,
        updated_at: Timestamp.now()
      });
      return { id, isTestUser };
    } catch (error) {
      console.error('Fehler beim Ändern der Tester-Freigabe:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Treuekarten-Verwaltung: Rabatt manuell hinzufügen
export const addLoyaltyDiscount = createAsyncThunk(
  'customers/addLoyaltyDiscount',
  async ({ customerId, amount = 1 }, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!isCentralRole(getCurrentUser(getState()))) {
        throw new Error('Nur die Zentrale darf Treuekarten verwalten');
      }

      const userRef = doc(db, 'users', customerId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }

      const userData = userDoc.data();
      const currentLoyaltyCard = userData.loyaltyCard || defaultLoyaltyCard;

      await updateDoc(userRef, {
        'loyaltyCard.availableDiscounts': (currentLoyaltyCard.availableDiscounts || 0) + amount,
        'loyaltyCard.totalEarnedDiscounts': (currentLoyaltyCard.totalEarnedDiscounts || 0) + amount,
        'loyaltyCard.lastUpdated': serverTimestamp()
      });

      // Aktualisierte Kundendaten laden
      dispatch(fetchCustomerById(customerId));

      return { customerId, amount };
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Rabatts:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Treuekarten-Verwaltung: Rabatt manuell entfernen
export const removeLoyaltyDiscount = createAsyncThunk(
  'customers/removeLoyaltyDiscount',
  async ({ customerId, amount = 1 }, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!isCentralRole(getCurrentUser(getState()))) {
        throw new Error('Nur die Zentrale darf Treuekarten verwalten');
      }

      const userRef = doc(db, 'users', customerId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }

      const userData = userDoc.data();
      const currentLoyaltyCard = userData.loyaltyCard || defaultLoyaltyCard;
      const currentAvailable = currentLoyaltyCard.availableDiscounts || 0;

      if (currentAvailable < amount) {
        throw new Error('Nicht genügend Rabatte verfügbar');
      }

      await updateDoc(userRef, {
        'loyaltyCard.availableDiscounts': currentAvailable - amount,
        'loyaltyCard.lastUpdated': serverTimestamp()
      });

      // Aktualisierte Kundendaten laden
      dispatch(fetchCustomerById(customerId));

      return { customerId, amount };
    } catch (error) {
      console.error('Fehler beim Entfernen des Rabatts:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Treuekarten-Verwaltung: Fortschritt anpassen
export const updateLoyaltyProgress = createAsyncThunk(
  'customers/updateLoyaltyProgress',
  async ({ customerId, newCount }, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!isCentralRole(getCurrentUser(getState()))) {
        throw new Error('Nur die Zentrale darf Treuekarten verwalten');
      }

      const userRef = doc(db, 'users', customerId);

      await updateDoc(userRef, {
        'loyaltyCard.currentRideCount': newCount,
        'loyaltyCard.lastUpdated': serverTimestamp()
      });

      // Aktualisierte Kundendaten laden
      dispatch(fetchCustomerById(customerId));

      return { customerId, newCount };
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Fortschritts:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Treuekarten-Verwaltung: Zurücksetzen
export const resetLoyaltyCard = createAsyncThunk(
  'customers/resetLoyaltyCard',
  async (customerId, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!isCentralRole(getCurrentUser(getState()))) {
        throw new Error('Nur die Zentrale darf Treuekarten verwalten');
      }

      const userRef = doc(db, 'users', customerId);

      await updateDoc(userRef, {
        'loyaltyCard': {
          currentRideCount: 0,
          availableDiscounts: 0,
          totalEarnedDiscounts: 0,
          totalRedeemedDiscounts: 0,
          totalSavingsEur: 0,
          lastRideCountedId: null,
          lastUpdated: serverTimestamp()
        }
      });

      // Aktualisierte Kundendaten laden
      dispatch(fetchCustomerById(customerId));

      return customerId;
    } catch (error) {
      console.error('Fehler beim Zurücksetzen der Treuekarte:', error);
      return rejectWithValue(error.message);
    }
  }
);

// ── Treuemeilen-Konto (System 2) – Verwaltung nur durch zentrale Admins ──

// Meilen manuell gutschreiben
export const addMiles = createAsyncThunk(
  'customers/addMiles',
  async ({ customerId, amount }, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!isCentralRole(getCurrentUser(getState()))) {
        throw new Error('Nur die Zentrale darf das Treuemeilen-Konto verwalten');
      }
      const userRef = doc(db, 'users', customerId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) throw new Error('Kunde nicht gefunden');

      const current = userDoc.data().milesCard || defaultMilesCard;
      await updateDoc(userRef, {
        'milesCard.balance': (current.balance || 0) + amount,
        'milesCard.totalEarned': (current.totalEarned || 0) + amount,
        'milesCard.lastUpdated': serverTimestamp()
      });

      dispatch(fetchCustomerById(customerId));
      return { customerId, amount };
    } catch (error) {
      console.error('Fehler beim Gutschreiben von Meilen:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Meilen manuell abziehen
export const removeMiles = createAsyncThunk(
  'customers/removeMiles',
  async ({ customerId, amount }, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!isCentralRole(getCurrentUser(getState()))) {
        throw new Error('Nur die Zentrale darf das Treuemeilen-Konto verwalten');
      }
      const userRef = doc(db, 'users', customerId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) throw new Error('Kunde nicht gefunden');

      const current = userDoc.data().milesCard || defaultMilesCard;
      const balance = current.balance || 0;
      if (balance < amount) throw new Error('Nicht genügend Meilen vorhanden');

      await updateDoc(userRef, {
        'milesCard.balance': balance - amount,
        'milesCard.lastUpdated': serverTimestamp()
      });

      dispatch(fetchCustomerById(customerId));
      return { customerId, amount };
    } catch (error) {
      console.error('Fehler beim Abziehen von Meilen:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Kontostand direkt setzen
export const setMilesBalance = createAsyncThunk(
  'customers/setMilesBalance',
  async ({ customerId, newBalance }, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!isCentralRole(getCurrentUser(getState()))) {
        throw new Error('Nur die Zentrale darf das Treuemeilen-Konto verwalten');
      }
      const userRef = doc(db, 'users', customerId);
      await updateDoc(userRef, {
        'milesCard.balance': Math.max(0, newBalance),
        'milesCard.lastUpdated': serverTimestamp()
      });

      dispatch(fetchCustomerById(customerId));
      return { customerId, newBalance };
    } catch (error) {
      console.error('Fehler beim Setzen des Meilen-Kontostands:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Treuemeilen-Konto zurücksetzen
export const resetMilesCard = createAsyncThunk(
  'customers/resetMilesCard',
  async (customerId, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!isCentralRole(getCurrentUser(getState()))) {
        throw new Error('Nur die Zentrale darf das Treuemeilen-Konto verwalten');
      }
      const userRef = doc(db, 'users', customerId);
      await updateDoc(userRef, {
        'milesCard': {
          balance: 0,
          totalEarned: 0,
          totalRedeemedBlocks: 0,
          totalSavingsEur: 0,
          lastRideCountedId: null,
          lastEarnedAt: null,
          lastUpdated: serverTimestamp()
        }
      });

      dispatch(fetchCustomerById(customerId));
      return customerId;
    } catch (error) {
      console.error('Fehler beim Zurücksetzen des Treuemeilen-Kontos:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Remote-Logout eines Kunden
export const logoutCustomer = createAsyncThunk(
  'customers/logoutCustomer',
  async (customerId, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'users', customerId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }
      
      // Remote-Logout-Flags setzen
      await updateDoc(userRef, {
        forceLogout: true,
        forceLogoutTimestamp: serverTimestamp()
      });
      
      return customerId;
    } catch (error) {
      console.error('Fehler beim Remote-Logout des Kunden:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Aktiver Listener-Speicher außerhalb des Reducer, um Cleanup zu ermöglichen
let customersStatusListener = null;

const initialState = {
  customers: [],
  currentCustomer: null,
  isLoading: false,
  error: null
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearCustomersError: (state) => {
      state.error = null;
    },
    clearCurrentCustomer: (state) => {
      state.currentCustomer = null;
    },
    updateCustomerRealtime: (state, action) => {
      const customerData = action.payload;
      const index = state.customers.findIndex(customer => customer.id === customerData.id);
      
      if (index !== -1) {
        // Nur die aktualisierten Felder übernehmen
        state.customers[index] = {
          ...state.customers[index],
          ...customerData
        };
      }
      
      // Auch den aktuellen Kunden aktualisieren, falls es derselbe ist
      if (state.currentCustomer && state.currentCustomer.id === customerData.id) {
        state.currentCustomer = {
          ...state.currentCustomer,
          ...customerData
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all customers
      .addCase(fetchCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch customer by ID
      .addCase(fetchCustomerById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCustomer = action.payload;
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create customer
      .addCase(createCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers.push(action.payload);
        state.currentCustomer = action.payload;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update customer
      .addCase(updateCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.customers.findIndex(customer => customer.id === action.payload.id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
        if (state.currentCustomer && state.currentCustomer.id === action.payload.id) {
          state.currentCustomer = action.payload;
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete customer
      .addCase(deleteCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = state.customers.filter(customer => customer.id !== action.payload);
        if (state.currentCustomer && state.currentCustomer.id === action.payload) {
          state.currentCustomer = null;
        }
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Toggle customer blocked
      .addCase(toggleCustomerBlocked.fulfilled, (state, action) => {
        const { id, isBlocked } = action.payload;
        const index = state.customers.findIndex(c => c.id === id);
        if (index !== -1) {
          state.customers[index] = { ...state.customers[index], isBlocked };
        }
        if (state.currentCustomer && state.currentCustomer.id === id) {
          state.currentCustomer = { ...state.currentCustomer, isBlocked };
        }
      })
      // Tester-Freigabe (Testmodus der Kunden-App)
      .addCase(toggleCustomerTestUser.fulfilled, (state, action) => {
        const { id, isTestUser } = action.payload;
        const index = state.customers.findIndex(c => c.id === id);
        if (index !== -1) {
          state.customers[index] = { ...state.customers[index], isTestUser };
        }
        if (state.currentCustomer && state.currentCustomer.id === id) {
          state.currentCustomer = { ...state.currentCustomer, isTestUser };
        }
      })
      .addCase(toggleCustomerTestUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Logout customer
      .addCase(logoutCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(logoutCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optional: Hier könnten wir den Online-Status des Kunden aktualisieren
        const index = state.customers.findIndex(customer => customer.id === action.payload);
        if (index !== -1) {
          state.customers[index].isOnline = false;
        }
      })
      .addCase(logoutCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

// Thunk zum Starten des Echtzeit-Listeners für Kunden-Status
export const startCustomersStatusListener = () => (dispatch) => {
  console.log('Starte Echtzeit-Listener für Kunden-Status...');
  
  // Existierenden Listener entfernen, falls vorhanden
  if (customersStatusListener) {
    console.log('Beende alten Kunden-Listener...');
    customersStatusListener();
    customersStatusListener = null;
  }
  
  // Listener für die Benutzersammlung einrichten (nur Kunden, keine Fahrer)
  const usersQuery = query(
    collection(db, 'users'),
    where('isDriver', '==', false)
  );
  
  customersStatusListener = onSnapshot(
    usersQuery,
    (snapshot) => {
      snapshot.docChanges().forEach(change => {
        const userData = change.doc.data();
        const userId = change.doc.id;
        
        // Nur bei neuen oder geänderten Dokumenten
        if (change.type === 'added' || change.type === 'modified') {
          console.log(`Echtzeit-Update für Kunde ${userId}:`, userData.isOnline);
          
          // Relevante Statusdaten extrahieren und an den Store senden
          dispatch({
            type: 'customers/updateCustomerRealtime',
            payload: {
              id: userId,
              isOnline: userData.isOnline || false,
              lastLogin: userData.lastLogin ? userData.lastLogin.toDate() : null
            }
          });
        }
      });
    },
    (error) => {
      console.error('Fehler im Kunden-Status-Listener:', error);
    }
  );
  
  return () => {
    // Cleanup-Funktion zurückgeben
    if (customersStatusListener) {
      customersStatusListener();
      customersStatusListener = null;
    }
  };
};

// Thunk zum Stoppen des Echtzeit-Listeners
export const stopCustomersStatusListener = () => () => {
  console.log('Stoppe Echtzeit-Listener für Kunden-Status...');
  if (customersStatusListener) {
    customersStatusListener();
    customersStatusListener = null;
  }
};

export const { clearCustomersError, clearCurrentCustomer, updateCustomerRealtime } = customersSlice.actions;
export default customersSlice.reducer;
